import {
  AgxAnimatedToggleLoaderOverlay,
  AgxButton,
  AgxDatePicker,
  AgxModal,
  AgxRadioGroup,
  AgxSpacer,
  AgxDivider,
  AgxTypography,
  Icon_AlertCircleOutline,
  Icon_FeaturedIcon,
  YesNo,
} from '@urbanx/agx.ui-components2';
import { useContractTerminate } from './useContractTerminate';
import { AgxFileUpload } from '@urbanx/agx-ui-components';
import { DocumentTypes, type TerminationAllocation } from '@urbanx/agx.campaigns.client';
import { deleteFile, getFileLink } from 'services';

interface Props {
  isOpen: boolean;
  setIsOpen: (_value: boolean) => void;
}
const ContractTerminateModal = (props: Props) => {
  const { isOpen, setIsOpen } = props;
  const {
    isCoolingOff,
    depositHeldInTrust,
    vendorChargesPenalty,
    allocation,
    allocationError,
    onDepositHeldChange,
    onVendorPenaltyChange,
    onTerminateConfirm,
    depositOptions,
    reasonOptions,
    canSubmit,
    onDateChange,
    onUploadFile,
    getAuthToken,
    selectedCampaign,
    submitting,
    vendorTerminationConfirmationFiles,
    buyerTerminationConfirmationFiles,
    onVendorTerminationConfirmationChanged,
    onBuyerTerminationConfirmationChanged,
    onDepositOptionChange,
    onReasonChange,
    selectedDepositOption,
    selectedReason,
    selectedTerminationDate,
  } = useContractTerminate({
    isOpen,
    setIsOpen,
  });

  return (
    <AgxModal
      title={'Mark as Terminated'}
      titleIcon={
        <>
          {submitting && (
            <AgxAnimatedToggleLoaderOverlay
              background="transparent"
              label="Terminating contract..."
            />
          )}
          <Icon_FeaturedIcon
            icon={<Icon_AlertCircleOutline type="Danger" />}
            color="var(--text-error)"
            backgroundColor="var(--bg-error-light)"
          />
        </>
      }
      type={'fullHeight'}
      variant={'withDivider'}
      actionButtonContainerStyle={'withDivider'}
      actionButtons={
        <AgxSpacer direction="horizontal" gap={'spacing-xl'} contentJustification="center" fill>
          <AgxButton
            id={'btnCancel'}
            text={'Cancel'}
            variant={'hollow'}
            onClick={() => setIsOpen(false)}
            stretch
          />
          <AgxButton
            id={'btnConfirm'}
            text={'Confirm'}
            variant={'primary'}
            danger
            disabled={!canSubmit}
            onClick={onTerminateConfirm}
            stretch
          />
        </AgxSpacer>
      }
      content={
        <AgxSpacer
          direction="vertical"
          gap={'md'}
          fill
          padding={{ top: 'md', bottom: 'md', left: 'xl', right: 'xl' }}
        >
          <AgxDatePicker
            id={'dateOfTermination'}
            label="Date of Termination"
            optionalText=""
            stretch
            required
            value={selectedTerminationDate}
            onChange={onDateChange}
          />
          <AgxFileUpload
            title="Vendor Termination Confirmation"
            id="fileVendorTerminationConfirmation"
            documentType={DocumentTypes.VendorTerminationConfirmation}
            buttonText={'Choose file'}
            fileData={vendorTerminationConfirmationFiles}
            onValueChanged={onVendorTerminationConfirmationChanged}
            getAuthToken={getAuthToken}
            campaignId={selectedCampaign?.campaignId || ''}
            uploadFile={onUploadFile}
            deleteFile={deleteFile}
            getFileLink={getFileLink}
            extensions={['pdf']}
            multiple={false}
            autoUpload={false}
            required
          />
          <AgxFileUpload
            title="Buyer Termination Confirmation"
            id="fileBuyerTerminationConfirmation"
            documentType={DocumentTypes.BuyerTerminationConfirmation}
            buttonText={'Choose file'}
            fileData={buyerTerminationConfirmationFiles}
            onValueChanged={onBuyerTerminationConfirmationChanged}
            getAuthToken={getAuthToken}
            campaignId={selectedCampaign?.campaignId || ''}
            uploadFile={onUploadFile}
            deleteFile={deleteFile}
            getFileLink={getFileLink}
            extensions={['pdf']}
            multiple={false}
            autoUpload={false}
            required
          />
          <AgxRadioGroup
            label="Reason"
            id={'reason'}
            layout="vertical"
            options={reasonOptions}
            value={selectedReason}
            onChange={onReasonChange}
            required
            subContentVariant="panel"
            subContent={
              isCoolingOff ? (
                <AgxSpacer key={selectedReason} direction="vertical" gap="spacing-xl" fill>
                  <AgxRadioGroup
                    id="depositHeldInTrust"
                    label="Is there deposit funds held in trust?"
                    layout="vertical"
                    options={[
                      { label: 'Yes', value: YesNo.Yes },
                      { label: 'No', value: YesNo.No },
                    ]}
                    value={
                      depositHeldInTrust === undefined
                        ? undefined
                        : depositHeldInTrust
                          ? YesNo.Yes
                          : YesNo.No
                    }
                    onChange={onDepositHeldChange}
                    required
                  />
                  {depositHeldInTrust === true && (
                    <>
                      <AgxRadioGroup
                        id="vendorChargesPenalty"
                        label="Is the Vendor charging the termination penalty?"
                        layout="vertical"
                        options={[
                          { label: 'Yes', value: YesNo.Yes },
                          { label: 'No', value: YesNo.No },
                        ]}
                        value={
                          vendorChargesPenalty === undefined
                            ? undefined
                            : vendorChargesPenalty
                              ? YesNo.Yes
                              : YesNo.No
                        }
                        onChange={onVendorPenaltyChange}
                        required
                      />
                      {vendorChargesPenalty === true && (
                        <TerminationBreakdown allocation={allocation} isError={allocationError} />
                      )}
                    </>
                  )}
                </AgxSpacer>
              ) : selectedReason ? (
                <AgxRadioGroup
                  key={selectedReason}
                  id="depositEntitlement"
                  label="Who is entitled to the deposit?"
                  layout="vertical"
                  options={depositOptions}
                  value={selectedDepositOption}
                  onChange={onDepositOptionChange}
                  required
                />
              ) : undefined
            }
          />
        </AgxSpacer>
      }
      isOpen={isOpen}
      setIsDialogOpen={setIsOpen}
    />
  );
};

export default ContractTerminateModal;

function TerminationBreakdown({
  allocation,
  isError,
}: {
  allocation: TerminationAllocation | undefined;
  isError: boolean;
}) {
  if (isError) return <p role="alert">Unable to load the termination allocation.</p>;
  if (!allocation) return <p role="status">Loading termination allocation...</p>;
  if (allocation.terminationPenalty == null || allocation.buyerBalance == null)
    return <p role="alert">A sale price is required to calculate the termination penalty.</p>;
  if (allocation.depositAmount == null)
    throw new Error('The termination allocation requires a deposit amount');
  if (allocation.earlyReleaseAmount == null)
    throw new Error('The termination allocation requires an early release amount');

  const rows = [
    { id: 'deposit', label: 'Total deposit', amount: allocation.depositAmount },
    ...(allocation.earlyReleaseAmount === 0
      ? []
      : [{ id: 'early-release', label: 'Early release', amount: -allocation.earlyReleaseAmount }]),
    {
      id: 'penalty',
      label: 'Termination penalty (.25% of sale price)',
      amount: allocation.terminationPenalty,
    },
    { id: 'balance', label: 'Balance to buyer', amount: allocation.buyerBalance },
  ];

  return (
    <AgxSpacer
      direction="vertical"
      gap="spacing-xl"
      padding="md"
      fill
      role="group"
      aria-label="Termination allocation"
      style={{
        background: 'var(--bg-secondary)',
        border: '1px solid var(--border-secondary)',
        borderRadius: 'var(--radius-2xl)',
      }}
    >
      {rows.map(({ id, label, amount }, index) => (
        <AgxSpacer key={id} direction="vertical" gap="spacing-xl" fill>
          {index > 0 && <AgxDivider variant="horizontal" color="var(--border-secondary)" />}
          <AgxSpacer
            direction="horizontal"
            contentJustification="space-between"
            contentAlignment="center"
            gap="spacing-xl"
            fill
          >
            <AgxTypography
              id={`termination-${id}-label`}
              type="body_ui"
              size="small"
              color="var(--text-tertiary)"
            >
              {label}
            </AgxTypography>
            <AgxTypography
              id={`termination-${id}-amount`}
              type="body_ui_strong"
              size="small"
              textAlign="right"
              noWrap
            >
              {amount.toLocaleString('en-AU', { style: 'currency', currency: 'AUD' })}
            </AgxTypography>
          </AgxSpacer>
        </AgxSpacer>
      ))}
    </AgxSpacer>
  );
}
